import type { BookType } from './common'

/**
 * Fee type configuration for threshold-based fees
 *
 */
export const BasicFeeTypes = [
  'Per Order',
  'Per Mile',
  'Per Hour',
  'Per Volume',
  'Per Retain',
  'Short Load',
  'Base Freight %',
  'Base & Accessorial %',
  'Total Freight %',
] as const

export type BasicFeeType = (typeof BasicFeeTypes)[number]

export const ThresholdFeeTypes = ['Per Stop', 'Per Drop', 'Per Load', 'Per No Load', 'Out of Route'] as const

export type ThresholdFeeType = (typeof ThresholdFeeTypes)[number]

export const TimeIntervalFeeTypes = ['Detention', 'Demurrage', 'Demurrage and Detention'] as const

export type TimeIntervalFeeType = (typeof TimeIntervalFeeTypes)[number]

export interface ThresholdFeeConfig {
  type: ThresholdFeeType
  threshold: number | null
}

/**
 * Fee type configuration for basic fees
 */
export interface BasicFeeConfig {
  type: BasicFeeType
}

export type TimeIntervalFeeConfig = {
  type: TimeIntervalFeeType
  threshold: number
  interval: number
  max_intervals: number
}

/**
 * Service rule definition
 */
export interface ServiceRule {
  type?: 'Weekend' | 'Holiday' | 'Night'
  threshold?: string | number
  threshold_type?: string
  enabled?: boolean
}

/**
 * Rate for an automatic accessorial
 */
export interface AutomaticAccessorialRate {
  id: string
  effective_from: string
  effective_to: string | null
  rate: number
  created_by: string
  created_on: string
  status: string
}

/**
 * Location reference with id and name
 */
export interface LocationReference {
  id: string
  name: string
}

/**
 * Location filter criteria
 */
export interface LocationFilters {
  locations: LocationReference[]
  site_states: string[]
  site_regions: LocationReference[]
  terminal_states: string[]
  terminal_regions: LocationReference[]
}

/**
 * Automatic accessorial definition
 *
 * Note: _id field comes from MongoDB in the backend API.
 * See ratebook.ts for details on why we maintain this naming pattern.
 */
export interface AutomaticAccessorial {
  _id: string | null
  name: string
  counterparty_id: string
  product_ids: string[]
  book_type: string
  product_group: string | null
  fee_type: ThresholdFeeConfig | BasicFeeConfig | TimeIntervalFeeConfig
  rates: AutomaticAccessorialRate[]
  rate_history: AutomaticAccessorialRate[]
  disabled: boolean
  requires_approval: boolean
  requires_reason_code: boolean
  exclude_from_invoice: boolean
  origin_name: string | null
  destination_name: string | null
  rate: number | null
  next_scheduled_rate: AutomaticAccessorialRate | null
  destination_ids: string[]
  origin_ids: string[]
  destinations: LocationFilters
  origins: LocationFilters
  service_rule?: ServiceRule
}

/**
 * Rate for a manual accessorial
 */
export interface ManualAccessorialRate {
  id?: string

  effective_from: string
  rate: number
  created_by: string
  created_on: string
  deleted_by?: string
  deleted_on?: string
  [key: string]: unknown
}

/**
 * Manual accessorial definition
 *
 * Note: Properties with underscores (_id, _destinations, _origins) come from
 * MongoDB/backend API naming conventions. See ratebook.ts for details.
 */
export interface ManualAccessorial {
  _id: string
  id?: string
  name: string
  fee_type: { type: string }
  description?: string
  product_group: string
  _destinations: Array<{ id: string; name: string }>
  _origins: Array<{ id: string; name: string }>
  rates: ManualAccessorialRate[]
  rate_history: ManualAccessorialRate[]
  disabled: boolean
  uom: string
  requires_approval: boolean
  requires_reason_code?: boolean
  freight_region_name?: string
  freight_region_id?: string
  rate: number
  next_scheduled_rate?: ManualAccessorialRate
  ratebook_id?: string
  linked_special_pay_config: {
    sp_id: string
    name: string
    link_date: string
    link_type: string | undefined
  }[]
  [key: string]: unknown
}

/**
 * Request to create an automatic accessorial
 */
export interface CreateAutomaticAccessorialPayload {
  _id: string | null
  name: string
  counterparty_id: string
  book_type: BookType
  product_group?: string | null
  product_ids?: string[]
  _destination_ids?: Array<string>
  _origin_ids?: Array<string>
  destination_ids?: Array<string>
  origin_ids?: Array<string>
  destinations?: LocationFilters
  origins?: LocationFilters
  fee_type: ThresholdFeeConfig | BasicFeeConfig | TimeIntervalFeeConfig
  rates: Array<{
    effective_from: string
    effective_to: string | null
    rate: number
    created_by: string | null
    created_on: string
  }>

  disabled: boolean
  requires_approval: boolean
  requires_reason_code: boolean
  exclude_from_invoice: boolean
  service_rule: ServiceRule | null
  linked_special_pay_config: {
    sp_id: string
    name: string
    link_date: string
    link_type: string | undefined
  }[]
}

/**
 * Request to create a manual accessorial
 */
export interface CreateManualAccessorialRequest {
  _id?: string
  name: string
  description: string
  counterparty_id: string
  book_type: BookType
  product_group: string | null
  destinations: LocationFilters
  destination_ids: string[]
  origins: LocationFilters
  rate: number
  origin_ids: string[]
  fee_type: {
    type: string
    threshold?: number
  }
  requires_approval: boolean
  requires_reason_code?: boolean
  exclude_from_invoice: boolean
  freight_region_name?: string
  freight_region_id?: string
  ratebook_id: string
  rates: Array<{
    effective_from: string
    rate: number
  }>
  linked_special_pay_config: {
    sp_id: string
    name: string
    link_date: string
    link_type: string | undefined
  }[]
}

/**
 * Request to create a manual accessorial rate
 */
export interface CreateManualAccessorialRateRequest {
  accessorial_id: string
  effective_from_date: string
  effective_to_date?: string
  rate: number
}

/**
 * Request to update a manual accessorial rate
 */
export interface UpdateManualAccessorialRateRequest {
  accessorial_id: string
  effective_from_date: string
  effective_to_date?: string
  rate: number
  rate_id: string
}

/**
 * Request to create an automatic accessorial rate
 */
export interface CreateAutomaticAccessorialRateRequest {
  accessorial_id: string
  effective_from_date: string
  effective_to_date: string | null
  rate: number
}

/**
 * Request to delete a manual accessorial rate
 */
export interface DeleteManualAccessorialRateRequest {
  accessorial_id: string
  effective_date: string
}

/**
 * Request to disable a manual accessorial
 */
export interface DisableManualAccessorialRequest {
  accessorial_id: string
  as_of?: string
}

/**
 * Request to disable an automatic accessorial
 */
export interface DisableAutomaticAccessorialRequest {
  accessorial_id: string
  as_of: string
}
